/**
 * Données publiques d'un cours, quand `GET class/{id}` ne les donne pas : visiteur déconnecté
 * (route réservée aux connectés, SPEC FC17) ou membre sans crédit applicable (401
 * ServicePurchaseRequired, SPEC FC11 : « le plan reste visible »).
 *
 * Lecture seule, routes projetées de la v2 (rien de personnel), appelées seulement dans ces deux cas :
 * 1. le cours déjà chargé par le planning (cache React Query, toutes formes de réponse) ;
 * 2. sinon `GET class/{id}/room` (plan de salle public, sans les Id Mindbody des autres clients), dont
 *    l'heure de début donne la semaine ISO, puis `GET class/planning` de cette semaine (même requête et
 *    même cache que le planning, sans e-mail ni téléphone des coachs) pour retrouver le cours.
 * La route v1 `GET class/{id}/pickaspot` n'est jamais appelée ici ; `GET class` (projetée en liste blanche depuis
 * la PR #147, ClassProjection) seulement par le repli de fetchPlanning, sur un 404 de `class/planning`
 * (serveur où la v2 est éteinte, page servie par un cache), comme le planning, qui l'appelait déjà en v1.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import { useMemo } from 'react';
import type { MindbodyClass, PickaspotClass } from 'types/class';
import { HTTP } from 'utils/http';
import { fetchPlanning, planningQueryKey } from '../../api/planning';
import { pickaspotLocalTime } from './lib/session';

/** Site du cours : `SiteId` (class/planning) ou `Location.SiteID` (GET class). */
const siteOf = (item: object): string | undefined => {
  const site = (item as { SiteId?: unknown }).SiteId ?? (item as MindbodyClass).Location?.SiteID;
  return site === undefined || site === null ? undefined : String(site);
};

const currentSiteId = (): string =>
  typeof mondayMindbody !== 'undefined' && mondayMindbody?.siteId ? String(mondayMindbody.siteId) : '';

/**
 * Les Id de cours sont propres à chaque site Mindbody : le planning multi-marques (cache `{ classes }` des 3 sites
 * Paris) peut contenir un cours RIISE ou Punch de même Id qu'un cours du site courant (constaté semaine 41/2026).
 * Un cours d'un autre site n'est donc jamais retenu.
 */
const looksLikeClass = (item: unknown, id: string, siteId = currentSiteId()): item is MindbodyClass =>
  !!item &&
  typeof item === 'object' &&
  String((item as MindbodyClass).Id) === id &&
  typeof (item as MindbodyClass).StartDateTime === 'string' &&
  !!(item as MindbodyClass).ClassDescription &&
  (siteId === '' || siteOf(item) === undefined || siteOf(item) === siteId);

/** Cherche un cours dans une donnée de cache : tableau, ou objet portant un tableau (ex. `{ classes }`). */
export function findClassIn(data: unknown, id: string, siteId = currentSiteId()): MindbodyClass | undefined {
  if (Array.isArray(data)) return data.find((item) => looksLikeClass(item, id, siteId));
  if (data && typeof data === 'object') {
    if (looksLikeClass(data, id, siteId)) return data;
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        const found = value.find((item) => looksLikeClass(item, id, siteId));
        if (found) return found;
      }
    }
  }
  return undefined;
}

export function usePublicClass(id: string | undefined, enabled: boolean) {
  const queryClient = useQueryClient();

  const cached = useMemo(() => {
    if (!enabled || !id) return undefined;
    for (const query of queryClient.getQueryCache().getAll()) {
      const found = findClassIn(query.state.data, id);
      if (found) return found;
    }
    return undefined;
  }, [enabled, id, queryClient]);

  const pick = useQuery<PickaspotClass | null>({
    queryKey: ['v2/class/public-room', id],
    queryFn: () => HTTP.get<PickaspotClass | null>(`/class/${id}/room`).send(),
    enabled: enabled && !!id,
    retry: false,
    staleTime: 0,
  });

  const start = cached?.StartDateTime ?? pickaspotLocalTime(pick.data?.classStartTime);
  const startDate = start ? new Date(start) : null;
  const week = startDate && !Number.isNaN(startDate.getTime()) ? getISOWeek(startDate) : undefined;
  const year = startDate && !Number.isNaN(startDate.getTime()) ? getISOWeekYear(startDate) : undefined;

  // = api/planning usePlanning (hors mode coach, marque du site) : même clé, donc même cache que le planning de la
  // semaine ; seuls les cours du site courant sont cherchés (looksLikeClass).
  const site = currentSiteId() || null;
  const list = useQuery({
    queryKey: planningQueryKey({ week: week ?? 0, year: year ?? 0, coachId: null, siteId: site }),
    queryFn: () =>
      fetchPlanning({ week: week as number, year: year as number, coachId: null, siteId: site, currentSiteId: currentSiteId() }),
    enabled: enabled && !cached && week !== undefined && year !== undefined,
    retry: false,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const cls = cached ?? (id ? findClassIn(list.data, id) : undefined);

  return {
    cls,
    pickASpot: pick.data && pick.data.roomLayout ? pick.data : undefined,
    isLoading: enabled && (pick.isLoading || (!cached && list.isLoading && list.fetchStatus !== 'idle')),
  };
}
